import { ApigeeAuthAdapter } from './AuthenticationService/authAdapters';
import authService from './authService';
import { Tokens } from './AuthenticationService/types';
import config from '../config';

/**
 * Mints the short-lived opaque tokens that api-edge-targeted consumers present.
 *
 * These are NOT the host session token. `authService` owns that one, it is minted
 * for the app's own client id, and it must keep resolving to whichever gateway
 * GraphQL uses. These are separate credentials for consumers whose requests go to
 * a gateway the host app is not currently using: the cs-widgets iframe, and
 * `/pge-bill-api/*`.
 *
 * Three properties this module exists to guarantee:
 *
 * 1. **The result is never written to `authService.tokensStorage`.**
 *    `ApigeeAuthAdapter.buildToken` returns `idToken: null`, and
 *    `TokenStorage.set()` dereferences `tokens.idToken?.token`, so storing one of
 *    these would corrupt the host session. They are held in module memory only.
 * 2. **Concurrent mints are deduped.** api-edge caps these at
 *    `MAX_TOKEN_TIMEOUT` — 1200s in prod, and less when the Cognito idToken
 *    expires sooner — so re-minting is routine rather than rare. `WidgetHost`
 *    races `ready` against the iframe's `onLoad` and the widget can emit
 *    `token-expiring` repeatedly, so without an in-flight guard one page could
 *    fan out an unbounded number of mints.
 * 3. **The cache is keyed by mint host, not just by consumer.** A single cache
 *    shared across hosts is a correctness bug, not an efficiency one: the host can
 *    change while a token is still unexpired, and the old token would then be
 *    served to the new gateway — which is exactly the wrong-gateway 403 this whole
 *    module exists to prevent. Keying by `clientId@baseUrl` makes that
 *    unrepresentable.
 */

/**
 * Re-mint this far ahead of expiry. Matches `TokenStorage.isExpiring()` so the two
 * halves of the app agree on what "about to expire" means.
 */
const REFRESH_MARGIN_MS = 300000;

const readIdToken = (): string => {
  try {
    return authService.tokensStorage?.get()?.idToken?.token ?? '';
  } catch {
    return '';
  }
};

const createMinter = (baseUrl: string, clientId: string) => {
  const adapter = new ApigeeAuthAdapter({ baseUrl, clientId });

  let cached: Tokens | null = null;
  let inFlight: Promise<Tokens> | null = null;

  const isUsable = (tokens: Tokens | null): boolean =>
    !!tokens &&
    !!tokens.accessToken?.token &&
    tokens.expiresAt - Date.now() > REFRESH_MARGIN_MS;

  return function mint(): Promise<Tokens> {
    if (isUsable(cached) && cached) {
      return Promise.resolve(cached);
    }
    if (inFlight) {
      return inFlight;
    }

    const idToken = readIdToken();
    if (!idToken) {
      // No signed-in session, so there is nothing to exchange. Failing here is
      // clearer than letting api-edge reject an empty `idp_access_token`, whose
      // 403 is indistinguishable from a misconfigured gateway.
      return Promise.reject(
        new Error('Cannot mint an edge token without a Cognito id token'),
      );
    }

    // Deliberately returned even when the mint lands already inside
    // REFRESH_MARGIN_MS: a short-lived Cognito idToken can cap the edge token
    // below the margin, and re-checking `isUsable` here would re-mint forever.
    const request = adapter
      .refreshTokens(idToken)
      .then(tokens => {
        cached = tokens;
        inFlight = null;
        return tokens;
      })
      .catch(error => {
        inFlight = null;
        throw error;
      });

    inFlight = request;
    return request;
  };
};

const minters = new Map<string, () => Promise<Tokens>>();

const minterFor = (baseUrl: string, clientId: string) => {
  const key = `${clientId}@${baseUrl}`;
  let mint = minters.get(key);
  if (!mint) {
    mint = createMinter(baseUrl, clientId);
    minters.set(key, mint);
  }
  return mint;
};

/**
 * Token postMessaged to the cs-widgets iframe. `baseUrl` is the gateway the widget
 * reported in its `ready` handshake, already validated against the known hosts by
 * `resolveWidgetMintBase` — never pass a raw postMessage value here.
 */
export const getWidgetEdgeToken = (baseUrl: string): Promise<Tokens> =>
  minterFor(baseUrl, config.apigee.widgetAuth.clientId)();

/** Token sent with `/pge-bill-api/*` requests, minted at that route's own host. */
export const getBillApiEdgeToken = (baseUrl: string): Promise<Tokens> =>
  minterFor(baseUrl, config.apigee.auth.clientId)();
